// react
import { useCallback, useMemo, useState } from 'react';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Editor } from '@/components/organisms/Editor/Editor';

// mui
import { Box, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { CareRecipientType, FieldType } from '@karehero/models';

export interface CareCircleSpec {
  careRecipientType: CareRecipientType;
  careRecipientName: string;
  careRecipientSymptomsAndConditions: string;
  careRecipientDateOfBirth: string;
  careRecipientPostcode: string;
  careRecipientConsent: boolean;
}

enum CareRecipientSymptomsAndConditions {
  Arthritis = 'arthritis',
  Cancer = 'cancer',
  Dementia = 'dementia',
  Diabetes = 'diabetes',
  HeartDisease = 'heart-disease',
  Hypertension = 'hypertension',
  MentalHealth = 'mental-health',
  Stroke = 'stroke',
  RespiratoryIllness = 'respiratory-illness',
  NoSymptoms = 'no-symptoms',
}

const careRecipientSymptomsAndConditionsOptions = [
  { value: CareRecipientSymptomsAndConditions.Arthritis, label: 'Arthritis' },
  { value: CareRecipientSymptomsAndConditions.Cancer, label: 'Cancer' },
  { value: CareRecipientSymptomsAndConditions.Dementia, label: 'Dementia' },
  { value: CareRecipientSymptomsAndConditions.Diabetes, label: 'Diabetes' },
  {
    value: CareRecipientSymptomsAndConditions.HeartDisease,
    label: 'Heart Disease',
  },
  {
    value: CareRecipientSymptomsAndConditions.Hypertension,
    label: 'Hypertension',
  },
  {
    value: CareRecipientSymptomsAndConditions.MentalHealth,
    label: 'Mental Health',
  },
  { value: CareRecipientSymptomsAndConditions.Stroke, label: 'Stroke' },
  {
    value: CareRecipientSymptomsAndConditions.RespiratoryIllness,
    label: 'Respiratory Illness',
  },
  {
    value: CareRecipientSymptomsAndConditions.NoSymptoms,
    label: 'No Symptoms',
  },
];

export const CareCircleCreate = ({
  onBack,
  onSave,
  onCancel,
  showToast,
  isAdmin = false,
}: CareCircleCreateProps) => {
  const theme = useTheme();
  const [value, setValue] = useState<CareCircleSpec>({
    careRecipientType: CareRecipientType.Other,
    careRecipientName: '',
    careRecipientSymptomsAndConditions: '[]',
    careRecipientDateOfBirth: '',
    careRecipientPostcode: '',
    careRecipientConsent: false,
  });

  const handleSave = useCallback(() => {
    console.log('[createCareCircle] creating care circle with value', value);
    if (!value.careRecipientConsent) {
      showToast(
        'Error',
        'You must have consent, or Power of Attorney, to proceed.',
        'error',
      );

      return;
    }
    onSave(value);
  }, [value, onSave, showToast]);

  const fieldsConfig = useMemo(() => {
    const consentText = isAdmin
      ? 'The user has confirmed they have consent, or Power of Attorney, to share sensitive information about the person to support, and authorise KareHero update the account.'
      : 'I have consent, or Power of Attorney, to share sensitive information about the person to support.';

    return {
      careRecipientType: {
        label: 'Relationship to person',
        isRequired: true,
        field: {
          type: FieldType.Select,
          options: [
            { label: 'Myself', value: CareRecipientType.Self },
            { label: 'Someone else', value: CareRecipientType.Other },
          ],
        },
      },

      // Only show the care recipient name field if the care recipient type is other.
      ...(value.careRecipientType === CareRecipientType.Other
        ? {
            careRecipientName: {
              label: 'First Name',
              field: {
                type: FieldType.Text,
              },
            },
            careRecipientConsent: {
              label: consentText,
              field: {
                type: FieldType.Boolean,
              },
              isRequired: true,
            },
          }
        : {}),

      careRecipientSymptomsAndConditions: {
        label: 'Health Status',
        field: {
          type: FieldType.MultiSelect,
          options: careRecipientSymptomsAndConditionsOptions.map((x) => ({
            value: x.value,
            label: x.label,
          })),
          isCustom: true,
        },
      },
      careRecipientDateOfBirth: {
        label: 'Date of Birth',
        field: {
          type: FieldType.DatePicker,
        },
      },
      careRecipientPostcode: {
        label: 'Postcode',
        field: {
          type: FieldType.Text,
        },
      },
    };
  }, [value, isAdmin]);

  const footer = (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        spacing={2}
        sx={{
          maxWidth: 'sm',
          mx: 'auto',
        }}
      >
        <Button variant='contained' size='large' fullWidth onClick={handleSave}>
          Save
        </Button>
        <Button variant='outlined' size='large' fullWidth onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );

  const content = (
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <PageHeader
          title='Add person to support'
          description='This helps create their profile and personalises next steps.'
        />
      }
      contentSlot={
        <Stack spacing={4}>
          <Editor
            isActionsDisabled
            isFloatingActions={false}
            value={value}
            onChange={(newValue) => {
              const newState = { ...newValue };
              setValue(newState);
            }}
            fields={fieldsConfig}
          />
        </Stack>
      }
    />
  );

  return (
    <ModalPageTemplate onBack={onBack} footerSlot={footer}>
      {content}
    </ModalPageTemplate>
  );
};

export interface CareCircleCreateProps {
  isAdmin?: boolean;
  onBack: () => void;
  showToast: (
    title: string,
    description: string,
    variant: 'success' | 'error',
  ) => void;
  onSave: (value: CareCircleSpec) => void;
  onCancel: () => void;
}

CareCircleCreate.displayName = 'CareCircleCreate';
export default CareCircleCreate;
